import { IColProps } from './Col';
import { Breakpoint, BreakpointMap, ColConfig, ColConfigObject, Gutter, RowColCount } from './types';

export const GRID_COMPONENTS_PREFIX = 'rio';
export const TOTAL_COL_SPAN = 12;

export const BREAKPOINT_ORDER: Breakpoint[] = ['xl', 'lg', 'md', 'smm', 'sm', 'xs'];

export const generateCommonRowClasses = <T>(property?: T | BreakpointMap<T>, prefixType?: string): string => {
  if (property === null || property === undefined) {
    return '';
  }

  const createClass = (value?: T | boolean, bp?: string): string => {
    const classValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
    return `${GRID_COMPONENTS_PREFIX}-${prefixType}-${bp ? `${bp}-` : ''}${classValue}`;
  };

  if (typeof property === 'string') {
    return createClass(property);
  }

  if (typeof property === 'boolean') {
    return createClass(property);
  }

  return Object.entries(property)
    .reduce<string[]>((classes, [bp, value]) => {
      if (value !== null && value !== undefined) {
        classes.push(createClass(value, bp));
      }
      return classes;
    }, [])
    .join(' ');
};

export const generateRowColCountClass = (colCount?: RowColCount): string => {
  if (colCount === 24) {
    return `${GRID_COMPONENTS_PREFIX}-row-col-24`;
  }
  return '';
};

export const generateRowGutterClasses = (gutter?: Gutter | BreakpointMap<Gutter>) => {
  if (!gutter) {
    return '';
  }

  if (typeof gutter === 'object' && !Array.isArray(gutter)) {
    return Object.entries(gutter)
      .map(([bp, value]) => {
        const [xGutter, yGutter] = Array.isArray(value) ? value : [value, value];
        const classes = [];

        if (typeof xGutter === 'string') {
          classes.push(`${GRID_COMPONENTS_PREFIX}-row-x-gutter-${bp}-${xGutter}`);
        }
        if (typeof yGutter === 'string') {
          classes.push(`${GRID_COMPONENTS_PREFIX}-row-y-gutter-${bp}-${yGutter}`);
        }

        return classes.join(' ');
      })
      .join(' ');
  }

  const [xGutter, yGutter] = Array.isArray(gutter) ? gutter : [gutter, gutter];
  const classes = [];

  if (typeof xGutter === 'string') {
    classes.push(`${GRID_COMPONENTS_PREFIX}-row-x-gutter-${xGutter}`);
  }

  if (typeof yGutter === 'string') {
    classes.push(`${GRID_COMPONENTS_PREFIX}-row-y-gutter-${yGutter}`);
  }

  return classes.join(' ');
};

export const generateColClasses = (span: ColConfigObject, breakpoints?: { [key in Breakpoint]?: ColConfigObject }) => {
  const classes: string[] = [`${GRID_COMPONENTS_PREFIX}-col`];

  const addClass = (prefix: string, value?: number | string | boolean) => {
    if (value !== undefined) {
      const normalizedValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
      classes.push(`${GRID_COMPONENTS_PREFIX}-${prefix}-${normalizedValue}`);
    }
  };
  const [leftOffset, rightOffset] = Array.isArray(span.offset) ? span.offset : [span.offset, span.offset];

  addClass('col', span.size);
  addClass('col-order', span.order);
  addClass('col-grow', span.grow);
  addClass('col-shrink', span.shrink);
  addClass('col-min', span.min);
  addClass('col-offset-x-left', leftOffset);
  addClass('col-offset-x-right', rightOffset);

  if (breakpoints) {
    Object.entries(breakpoints).forEach(([bp, value]) => {
      if (value) {
        addClass(`col-${bp}`, value.size);
        addClass(`col-order-${bp}`, value.order);
        addClass(`col-grow-${bp}`, value.grow);
        addClass(`col-shrink-${bp}`, value.shrink);
        addClass(`col-min-${bp}`, value.min);
        const [leftOffset, rightOffset] = Array.isArray(value.offset) ? value.offset : [value.offset, value.offset];
        addClass(`col-offset-x-left-${bp}`, leftOffset);
        addClass(`col-offset-x-right-${bp}`, rightOffset);
      }
    });
  }

  return classes;
};

// Utility function to find the first valid value from the current breakpoint downwards
export const findBreakpointValue = <T>(
  breakpoint: Breakpoint,
  values: Partial<Record<Breakpoint, T>>,
  breakpointsPriority: Breakpoint[] = ['xl', 'lg', 'md', 'smm', 'sm', 'xs']
): T | undefined => {
  return breakpointsPriority
    .slice(breakpointsPriority.indexOf(breakpoint))
    .map((bp) => values[bp])
    .find((value) => value !== undefined);
};

export const getSafeColConfigSize = (config: ColConfig | undefined): number => {
  if (typeof config === 'number') {
    return config;
  }

  if (config && typeof config === 'object') {
    return typeof config.size === 'number' ? config.size : 0;
  }

  return 0;
};

export const getColConfigByBreakpoint = (colProps: IColProps, breakpoint: Breakpoint): ColConfig | undefined => {
  const breakpointIndex = BREAKPOINT_ORDER.indexOf(breakpoint);

  for (let i = breakpointIndex; i < BREAKPOINT_ORDER.length; i++) {
    const bp = BREAKPOINT_ORDER[i];
    const config = colProps[bp];
    if (config) {
      return config;
    }
  }

  return colProps.size;
};
